import { useRef, useCallback } from "react";

/**
 * Swipe gesture hook for list items.
 * Returns handlers to attach to a list item element.
 *
 * Usage:
 *   const swipe = useSwipeAction({ onSwipeRight: () => markDone(), onSwipeLeft: () => snooze() });
 *   <div {...swipe.handlers} style={swipe.style}>content</div>
 */
export default function useSwipeAction({ onSwipeRight, onSwipeLeft, threshold = 80 }) {
  const startX = useRef(0);
  const currentX = useRef(0);
  const swiping = useRef(false);
  const elRef = useRef(null);

  const onTouchStart = useCallback((e) => {
    if (window.innerWidth > 768) return;
    startX.current = e.touches[0].clientX;
    currentX.current = 0;
    swiping.current = true;
    elRef.current = e.currentTarget;
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!swiping.current || !elRef.current) return;
    const diff = e.touches[0].clientX - startX.current;
    currentX.current = diff;

    // Only allow swipe if we have a handler for that direction
    if (diff > 0 && !onSwipeRight) return;
    if (diff < 0 && !onSwipeLeft) return;

    const clamped = Math.max(-120, Math.min(120, diff));
    elRef.current.style.transform = `translateX(${clamped}px)`;
    elRef.current.style.transition = 'none';

    // Show color hint
    if (clamped > threshold * 0.5) {
      elRef.current.style.background = 'rgba(34,197,94,0.08)'; // green hint
    } else if (clamped < -threshold * 0.5) {
      elRef.current.style.background = 'rgba(239,68,68,0.08)'; // red hint
    } else {
      elRef.current.style.background = '';
    }
  }, [onSwipeRight, onSwipeLeft, threshold]);

  const onTouchEnd = useCallback(() => {
    if (!swiping.current || !elRef.current) return;
    swiping.current = false;
    const el = elRef.current;

    el.style.transition = 'transform .2s ease-out, background .2s';
    el.style.transform = 'translateX(0)';
    el.style.background = '';

    if (currentX.current > threshold && onSwipeRight) {
      // Animate out right then callback
      el.style.transform = 'translateX(100%)';
      el.style.opacity = '0.5';
      setTimeout(() => {
        el.style.transform = 'translateX(0)';
        el.style.opacity = '1';
        onSwipeRight();
      }, 200);
    } else if (currentX.current < -threshold && onSwipeLeft) {
      el.style.transform = 'translateX(-100%)';
      el.style.opacity = '0.5';
      setTimeout(() => {
        el.style.transform = 'translateX(0)';
        el.style.opacity = '1';
        onSwipeLeft();
      }, 200);
    }

    elRef.current = null;
  }, [onSwipeRight, onSwipeLeft, threshold]);

  return {
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    },
  };
}
